import React, { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { FaCcStripe } from 'react-icons/fa';
import styled from 'styled-components';

import { generalErrorHandler } from '../../actions/utils';
import LoadingImage from '../../components/general/LoadingImage';
import useDispatch from '../../hooks/useDispatch';
import { transactionService } from '../../services';

const Styles = styled.div`
  margin-top: 5px;
  margin-bottom: 30px;

  button {
    width: 100%;
  }
`;

interface Props {
  priceId: string;
  isSandbox: boolean;
  hasTrial?: boolean;
  promo?: string;
  isStripeNativePromo?: boolean;
  onCompleted?: () => void;
  oneTimePayment?: boolean;
  switchFromManual?: boolean;
  metadata?: Record<string, string>;
  buttonTextId?: string;
}

/**
 * The checkout button for stripe. Clicking it will redirect
 * user to a page setup by Stripe to complete the process.
 */
const StripeCheckoutButton: React.FC<Props> = ({
  priceId,
  isSandbox,
  hasTrial,
  promo,
  isStripeNativePromo,
  onCompleted,
  oneTimePayment,
  switchFromManual,
  metadata,
  buttonTextId,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [redirectUri, setRedirectUri] = useState<string>();

  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    setLoading(true);
    transactionService
      .createStripeSession(
        priceId,
        hasTrial,
        isSandbox,
        promo,
        isStripeNativePromo,
        oneTimePayment,
        switchFromManual,
        metadata
      )
      .then(data => {
        setLoading(false);
        onCompleted && onCompleted();
        if (data?.url) {
          setRedirectUri(data?.url);
          window.location.assign(data?.url);
        }
      })
      .catch(error => {
        setLoading(false);
        generalErrorHandler(dispatch)(error);
      });
  }, [
    dispatch,
    hasTrial,
    isSandbox,
    isStripeNativePromo,
    metadata,
    onCompleted,
    oneTimePayment,
    priceId,
    promo,
    switchFromManual,
  ]);

  // reset the redirect uri when plan changes
  useEffect(() => {
    setRedirectUri(null);
  }, [
    hasTrial,
    isSandbox,
    isStripeNativePromo,
    metadata,
    onCompleted,
    oneTimePayment,
    priceId,
    promo,
  ]);

  return (
    <Styles>
      <Button
        className="stripe-button"
        size="lg"
        onClick={handleClick}
        disabled={loading}
      >
        <b>
          <FaCcStripe style={{ height: '1.4em', width: '1.4em' }} />{' '}
          {t(buttonTextId ?? 'stripe-checkout-button-text')}
        </b>
        <LoadingImage loading={loading} />
      </Button>
      {redirectUri && (
        <div className="text-center">
          <small>
            <small>
              <a href={redirectUri} target="_blank" rel="noreferrer">
                {t('click-to-redirect-if-not')}
              </a>
            </small>
          </small>
        </div>
      )}
    </Styles>
  );
};

export default StripeCheckoutButton;
